/**
 * Этапы шагов процедуры — общие хелперы для десктопа и мобильной версии.
 *
 * Принцип: этапы — только метаданные группировки. Источник правды по шагам
 * остаётся плоским списком `scenario.steps`; порядок = порядок этапов (по `index`),
 * внутри этапа — по `index` шага. Такая схема работает независимо от того,
 * является ли `step.index` глобальным по процедуре или локальным внутри этапа.
 *
 * Обратная совместимость: процедура без этапов (`modules` пустой/отсутствует)
 * представляется одним виртуальным этапом «Шаги» с `id = null` — ничего
 * на бэкенд при этом не пишется.
 */

import type { Scenario, ScenarioModule, ScenarioStep } from '@/lib/api-v3'

/** Имя этапа по умолчанию (и виртуального legacy-этапа) */
export const DEFAULT_MODULE_NAME = 'Шаги'

/** Имя этапа, добавленного пользователем (по аналогии с «Новый шаг») */
export const NEW_MODULE_NAME = 'Новый этап'

/** ID виртуального блока для процедур без этапов */
export const LEGACY_BLOCK_ID = 'block-legacy'

/** Префикс ID UI-блока реального этапа */
export const MODULE_BLOCK_PREFIX = 'module-'

/** Нормализованное описание этапа для UI */
export interface NormalizedModule {
  /** ID этапа на бэкенде; null — виртуальный legacy-этап */
  id: number | null
  /** ID UI-блока (`module-{id}` или `block-legacy`) */
  blockId: string
  name: string
  index: number
  /** Шаги этапа в порядке прохождения (уже отсортированы, без удалённых) */
  steps: ScenarioStep[]
}

export interface NormalizedScenarioModules {
  modules: NormalizedModule[]
  /** Все шаги процедуры в едином порядке прохождения */
  orderedSteps: ScenarioStep[]
  /** true — процедура без реальных этапов (один виртуальный блок) */
  isLegacy: boolean
}

export const moduleBlockId = (moduleId: number | null | undefined): string =>
  moduleId == null ? LEGACY_BLOCK_ID : `${MODULE_BLOCK_PREFIX}${moduleId}`

/** Обратное преобразование: ID UI-блока → ID этапа (null для legacy/неизвестного) */
export const moduleIdFromBlockId = (blockId: string | null | undefined): number | null => {
  if (!blockId || !blockId.startsWith(MODULE_BLOCK_PREFIX)) return null
  const n = parseInt(blockId.slice(MODULE_BLOCK_PREFIX.length), 10)
  return Number.isNaN(n) ? null : n
}

const byIndex = <T extends { index?: number | null }>(a: T, b: T) => (a.index ?? 0) - (b.index ?? 0)

const isActive = (s: { objectState?: number | null }) => (s.objectState ?? 0) === 0

/** Активные этапы процедуры, отсортированные по index. Пустой массив для legacy */
export const getActiveModules = (scenario: Pick<Scenario, 'modules'> | null | undefined): ScenarioModule[] => {
  const raw = scenario?.modules
  if (!Array.isArray(raw) || raw.length === 0) return []
  return raw.filter(isActive).sort(byIndex)
}

/**
 * Единый список шагов процедуры: плоский `steps` + шаги из `modules[].steps`.
 *
 * Проверено на dev-бэкенде (см. комментарий в конце файла): GET /scenarios/{id} отдаёт в `steps`
 * только шаги без этапа, а шаги этапов — внутри `modules[].steps` (с полными forms/attachments).
 * После `withOrderedSteps` шаги этапов уже лежат в плоском списке, а `modules[].steps` очищены —
 * повторный вызов ничего не дублирует.
 */
export const collectAllSteps = (scenario: Pick<Scenario, 'steps' | 'modules'> | null | undefined): ScenarioStep[] => {
  const flat = scenario?.steps ?? []
  const seen = new Set(flat.map(s => s.id))
  const result = [...flat]
  for (const m of scenario?.modules ?? []) {
    for (const s of m.steps ?? []) {
      if (seen.has(s.id)) continue
      seen.add(s.id)
      result.push(s.moduleId == null ? { ...s, moduleId: m.id } : s)
    }
  }
  return result
}

/**
 * Разложить плоские шаги по этапам.
 *
 * @param scenario — процедура (нужны `steps` и `modules`)
 * @param options.includeDeleted — не отфильтровывать шаги с objectState !== 0 (по умолчанию фильтруем)
 */
export const normalizeScenarioModules = (
  scenario: Pick<Scenario, 'steps' | 'modules'> | null | undefined,
  options?: { includeDeleted?: boolean }
): NormalizedScenarioModules => {
  const allSteps = collectAllSteps(scenario).filter(s => options?.includeDeleted || isActive(s))
  const modules = getActiveModules(scenario)

  // Legacy: нет этапов → один виртуальный блок со всеми шагами
  if (modules.length === 0) {
    const steps = [...allSteps].sort(byIndex)
    return {
      isLegacy: true,
      orderedSteps: steps,
      modules: [{ id: null, blockId: LEGACY_BLOCK_ID, name: DEFAULT_MODULE_NAME, index: 0, steps }],
    }
  }

  const byModule = new Map<number, ScenarioStep[]>()
  modules.forEach(m => byModule.set(m.id, []))
  const orphans: ScenarioStep[] = []

  for (const step of allSteps) {
    const mid = step.moduleId
    if (mid != null && byModule.has(mid)) {
      byModule.get(mid)!.push(step)
    } else {
      // moduleId null или ссылается на несуществующий/удалённый этап — кладём в первый этап
      orphans.push(step)
    }
  }

  const normalized: NormalizedModule[] = modules.map((m, i) => ({
    id: m.id,
    blockId: moduleBlockId(m.id),
    name: m.name || DEFAULT_MODULE_NAME,
    index: m.index ?? i,
    steps: (byModule.get(m.id) ?? []).sort(byIndex),
  }))

  if (orphans.length > 0 && normalized.length > 0) {
    normalized[0].steps = [...orphans.sort(byIndex), ...normalized[0].steps]
  }

  return {
    isLegacy: false,
    modules: normalized,
    orderedSteps: normalized.flatMap(m => m.steps),
  }
}

/**
 * Единый порядок шагов процедуры (замена `steps.sort(by index)`).
 * Для процедур без этапов результат идентичен сортировке по index.
 */
export const getOrderedSteps = (
  scenario: Pick<Scenario, 'steps' | 'modules'> | null | undefined,
  options?: { includeDeleted?: boolean }
): ScenarioStep[] => normalizeScenarioModules(scenario, options).orderedSteps

/**
 * Вернуть копию процедуры с `steps`, переупорядоченными в порядке прохождения.
 * Шаги, не попавшие в порядок (удалённые при includeDeleted=false), сохраняются в конце.
 */
export const withOrderedSteps = <T extends Pick<Scenario, 'steps' | 'modules'>>(
  scenario: T,
  options?: { includeDeleted?: boolean }
): T => {
  const ordered = getOrderedSteps(scenario, options)
  const orderedIds = new Set(ordered.map(s => s.id))
  const rest = collectAllSteps(scenario).filter(s => !orderedIds.has(s.id))
  // modules[].steps очищаем: единственный источник правды по шагам — плоский список
  const modules = Array.isArray(scenario.modules)
    ? scenario.modules.map(m => (m.steps ? { ...m, steps: undefined } : m))
    : scenario.modules
  return { ...scenario, steps: [...ordered, ...rest], modules }
}

/**
 * Сортировка ПЛОСКОГО списка шагов без доступа к `scenario.modules`
 * (отчёты, PDF, копилот — им приходит только `ScenarioStep[]`).
 *
 * Если все шаги принадлежат одному этапу (или legacy без moduleId) — сортируем по index,
 * как раньше. Если шаги из разных этапов — доверяем входному порядку (он уже собран
 * через getOrderedSteps/withOrderedSteps на уровне загрузки процедуры), т.к. index
 * может быть локальным внутри этапа и глобальная сортировка по нему сломает порядок.
 */
export const sortStepsForDisplay = <T extends { index?: number | null; moduleId?: number | null }>(steps: T[]): T[] => {
  const list = [...steps]
  const moduleIds = new Set(list.map(s => s.moduleId ?? null))
  if (moduleIds.size <= 1) return list.sort(byIndex)
  return list
}

/** Найти этап, содержащий шаг с данным глобальным индексом (в orderedSteps) */
export const getModuleForStepIndex = (
  normalized: NormalizedScenarioModules,
  globalStepIndex: number
): { module: NormalizedModule; moduleIndex: number; stepIndexInModule: number } | null => {
  let offset = 0
  for (let i = 0; i < normalized.modules.length; i++) {
    const m = normalized.modules[i]
    if (globalStepIndex < offset + m.steps.length) {
      return { module: m, moduleIndex: i, stepIndexInModule: globalStepIndex - offset }
    }
    offset += m.steps.length
  }
  return null
}

/** Найти этап по ID шага */
export const getModuleForStepId = (
  normalized: NormalizedScenarioModules,
  stepId: number
): NormalizedModule | null =>
  normalized.modules.find(m => m.steps.some(s => s.id === stepId)) ?? null

/**
 * Собрать UI-блоки (по одному на этап) из процедуры.
 *
 * @param mapStep — преобразование шага в UI-элемент; получает глобальный индекс (сквозной по процедуре —
 *                  для прохождения/`?step=`), предыдущий UI-элемент с тем же id (для сохранения
 *                  `completed` и т.п.) и локальный индекс внутри этапа (для отображаемого номера)
 * @param prevBlocks — предыдущие блоки, чтобы переиспользовать состояние UI-шагов
 */
export const buildStepBlocks = <TStep extends { id: string | number }, TBlock extends { id: string; name: string; steps: TStep[] }>(
  scenario: Pick<Scenario, 'steps' | 'modules'> | null | undefined,
  mapStep: (step: ScenarioStep, globalIndex: number, prev: TStep | undefined, localIndex: number) => TStep,
  makeBlock: (module: NormalizedModule, steps: TStep[]) => TBlock,
  prevBlocks?: TBlock[] | null,
  options?: { includeDeleted?: boolean; stepUiId?: (step: ScenarioStep) => string | number }
): { blocks: TBlock[]; normalized: NormalizedScenarioModules } => {
  const stepUiId = options?.stepUiId ?? ((s: ScenarioStep) => `step-${s.id}`)
  const normalized = normalizeScenarioModules(scenario, { includeDeleted: options?.includeDeleted })
  const prevMap = new Map<string | number, TStep>()
  prevBlocks?.forEach(b => b.steps.forEach(s => prevMap.set(s.id, s)))

  let globalIndex = 0
  const blocks = normalized.modules.map(module => {
    const steps = module.steps.map((step, localIndex) => {
      const ui = mapStep(step, globalIndex, prevMap.get(stepUiId(step)), localIndex)
      globalIndex += 1
      return ui
    })
    return makeBlock(module, steps)
  })

  return { blocks, normalized }
}

/** Отображаемый номер шага: две цифры с ведущим нулём («01», «12») */
export const formatStepNumber = (indexFrom0: number): string => String(indexFrom0 + 1).padStart(2, '0')

// ─── Отображение шагов в отчётах / PDF ─────────────────────────────────────────

export interface StepDisplayInfo {
  /** Номер шага внутри этапа (с 1) — так шаг подписан в интерфейсе */
  number: number
  /** Номер для отчётов: «1.2» (этап.шаг) при нескольких этапах, иначе «2» */
  displayNumber: string
  /** Сквозной номер по процедуре (с 1) */
  globalNumber: number
  /** Порядковый номер этапа (с 0) */
  moduleIndex: number
  moduleId: number | null
  moduleName: string
}

export interface StepDisplayModule {
  id: number | null
  index: number
  name: string
  stepIds: number[]
}

export interface StepDisplayIndex {
  byStepId: Map<number, StepDisplayInfo>
  modules: StepDisplayModule[]
  /**
   * Показывать ли этапы в интерфейсе отчёта. false для legacy-процедур и процедур
   * с единственным этапом «Шаги» — там отчёт выглядит как раньше.
   */
  hasModules: boolean
  /** Подпись этапа вида «1. Название» */
  moduleLabel: (moduleIndex: number) => string
  /** Подпись шага: «Шаг 2», а при нескольких этапах — «Шаг 1.2» */
  stepLabel: (stepId: number) => string
}

/**
 * Индекс отображения шагов для отчётов и PDF: номер внутри этапа, принадлежность к этапу,
 * подписи. Работает по плоскому списку шагов + этапам (оба берутся из снапшота/процедуры).
 */
export const buildStepDisplayIndex = (
  steps: ScenarioStep[] | null | undefined,
  modules: ScenarioModule[] | null | undefined,
  options?: { includeDeleted?: boolean }
): StepDisplayIndex => {
  const normalized = normalizeScenarioModules({ steps: steps ?? [], modules: modules ?? null }, options)
  const displayModules: StepDisplayModule[] = normalized.modules.map((m, moduleIndex) => (
    { id: m.id, index: moduleIndex, name: m.name, stepIds: m.steps.map(s => s.id) }
  ))
  const hasModules = displayModules.length > 1
    || (displayModules.length === 1 && displayModules[0].id != null && displayModules[0].name.trim() !== DEFAULT_MODULE_NAME)
  const byStepId = new Map<number, StepDisplayInfo>()
  let globalNumber = 0
  normalized.modules.forEach((m, moduleIndex) => {
    m.steps.forEach((s, i) => {
      globalNumber += 1
      const number = i + 1
      byStepId.set(s.id, {
        number,
        displayNumber: hasModules ? `${moduleIndex + 1}.${number}` : String(number),
        globalNumber, moduleIndex, moduleId: m.id, moduleName: m.name,
      })
    })
  })
  const moduleLabel = (moduleIndex: number) => {
    const m = displayModules[moduleIndex]
    return m ? `${moduleIndex + 1}. ${m.name}` : ''
  }
  const stepLabel = (stepId: number) => {
    const info = byStepId.get(stepId)
    return info ? `Шаг ${info.displayNumber}` : ''
  }
  return { byStepId, modules: displayModules, hasModules, moduleLabel, stepLabel }
}

/** Переход между этапами в хронологии отчёта: предыдущий этап (если был) и новый */
export interface ModuleTransition {
  /** Индекс этапа, из которого ушли; null — начало прохождения («Этап N начат») */
  fromIndex: number | null
  /** Все шаги предыдущего этапа отмечены выполненными к моменту перехода */
  fromCompleted: boolean
  toIndex: number
}

/**
 * Вставить маркеры переходов между этапами в хронологию (по аналогии с паузами):
 * перед первым посещённым шагом («Этап N начат») и перед каждым посещённым шагом,
 * этап которого отличается от этапа предыдущего посещённого шага. Паузы и пропущенные
 * шаги (они идут в конце) маркеров не порождают. Для процедур без этапов список
 * возвращается как есть.
 */
export const withModuleTransitions = <T extends { stepId: number; isPause?: boolean; skipped?: boolean; marked?: boolean }>(
  items: T[],
  stepDisplay: StepDisplayIndex,
  makeMarker: (t: ModuleTransition) => T,
): T[] => {
  if (!stepDisplay.hasModules) return items
  const result: T[] = []
  let currentModule: number | null = null
  const markedIds = new Set<number>()
  for (const it of items) {
    if (!it.isPause && !it.skipped) {
      const info = stepDisplay.byStepId.get(it.stepId)
      if (info && info.moduleIndex !== currentModule) {
        const prevIds = currentModule != null ? stepDisplay.modules[currentModule]?.stepIds ?? [] : []
        const fromCompleted = currentModule != null && prevIds.length > 0 && prevIds.every(id => markedIds.has(id))
        result.push(makeMarker({ fromIndex: currentModule, fromCompleted, toIndex: info.moduleIndex }))
        currentModule = info.moduleIndex
      }
      if (it.marked) markedIds.add(it.stepId)
    }
    result.push(it)
  }
  return result
}

/** Текст маркера: «Этап 1 начат» / «Этап 1 завершён · Переход к этапу 2» / «Переход к этапу 2» */
export const moduleTransitionLabel = (t: ModuleTransition): string => {
  if (t.fromIndex == null) return `Этап ${t.toIndex + 1} начат`
  const to = `Переход к этапу ${t.toIndex + 1}`
  return t.fromCompleted ? `Этап ${t.fromIndex + 1} завершён · ${to}` : to
}

/*
 * Результаты проверки dev-бэкенда (dev.api.casper-ai.ru, 2026-09-17):
 * 1. `step.index` — ЛОКАЛЬНЫЙ внутри этапа (в каждом этапе начинается с 0). Шаги без этапа
 *    (moduleId = null) индексируются отдельно и остаются в плоском `steps`.
 * 2. GET /scenarios/{id}: `steps` содержит только шаги без этапа, шаги этапов — в `modules[].steps`
 *    (с полными forms/attachments/incidents и moduleId). У процедуры без этапов `modules: []`.
 *    `stepsCount` считает все шаги. GET /scenarios/{id}/steps → 404 (нет такого эндпоинта).
 * 3. DELETE /scenarios/modules/{id} с шагами → 400 "Scenario module has attached steps" — шаги
 *    нужно удалять явно перед этапом (так и делаем).
 * 4. POST /scenarios/steps/{id}/copy сохраняет moduleId оригинала, копия добавляется в конец этапа.
 * 5. POST /scenarios/steps/{id}/update { moduleId, index } переносит шаг между этапами и сам
 *    переиндексирует оба этапа. modules/{id}/steps/indexes и {scenarioId}/modules/indexes — 200.
 * 6. versions/snapshots для свежей процедуры → 404 (снапшоты появляются после сессий редактирования);
 *    парсинг снапшота идёт через withOrderedSteps, поэтому оба варианта состава поддерживаются.
 */
